import type { DocumentView } from '@myrewards/contracts';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { readStagedFile, stageFile, unstageFile } from '../api/actingFor';
import { isApiError } from '../api/errors';
import {
  actForProblemWords,
  isBlocked,
  MAX_FILE_BYTES,
  MAX_FILES,
  scanPollDelay,
  TOO_BIG,
  type FileSource,
} from './words';

/** One file in the step: on its way up, staged (with the server's document), or refused. */
export interface StagedItem {
  /** The step's own key for the row (the document's id isn't known until it's staged). */
  key: string;
  /** The name it was added with; the server's name once it's staged. */
  name: string;
  sizeBytes: number;
  /** What it is ("image/png"), for its icon. */
  contentType: string;
  source: FileSource;
  state: 'uploading' | 'staged' | 'failed';
  document: DocumentView | null;
  /** Why it was refused, or why removing it didn't work. */
  error: string | null;
  removing: boolean;
  /**
   * Its check can't be read any more (the server has no such staged file): it isn't read again,
   * and it holds the action until it's removed, as a blocked file does (R57).
   */
  unreadable: boolean;
}

export interface StagedFiles {
  items: readonly StagedItem[];
  /** Stages each file at once (D14), up to the action's 20. */
  add: (files: readonly File[], source: FileSource) => void;
  /** Removes a row: a staged file is deleted on the server; one still uploading is when it lands. */
  remove: (key: string) => Promise<void>;
  /** How many more files the action can carry. */
  room: number;
  /** Something is still uploading or being removed: the action can't name it yet. */
  busy: boolean;
  /**
   * A file the virus check refused, or whose check can't be read, is in the list: the action
   * waits until it's removed.
   */
  blocked: boolean;
  /** The staged documents, in the order they were added: what the action attaches. */
  documents: readonly DocumentView[];
  /**
   * Saves the action with these files. While it's on its way nothing is given up; if it's
   * refused, the files stay the step's, and if the step has gone meanwhile, they're deleted then.
   * Once it succeeds, they're the action's and are never deleted from here.
   */
  confirm: <T>(save: () => Promise<T>) => Promise<T>;
  /** Gives up every staged file (Cancel): each is deleted, and one still uploading when it lands. */
  discard: () => void;
}

/**
 * The step's own files (APP-148 D14). Each step that's opened starts empty and stages its own
 * uploads, so a file staged for one action is never reused by another (review focus 2): a second
 * action needs its own copy. A pending virus check is read again every 4 s until it's done (R47);
 * a read that fails backs off, and a file the server no longer has stops being read (R57).
 *
 * Nothing it staged is left behind: Remove, Cancel, and the step going away without confirming
 * (the page moved on) each delete what was staged, including an upload still on its way. Files
 * attached by a confirmed action are never deleted.
 */
export function useStagedFiles(): StagedFiles {
  const [items, setItems] = useState<StagedItem[]>([]);
  // What the list holds now, for the callbacks and the unmount, which outlive a render.
  const latest = useRef<StagedItem[]>([]);
  useLayoutEffect(() => {
    latest.current = items;
  }, [items]);
  const counter = useRef(0);
  /** Rows given up before their upload landed: the upload is deleted as soon as it does. */
  const givenUp = useRef(new Set<string>());
  /** Everything is given up (cancelled, or gone without confirming). */
  const discarded = useRef(false);
  /** The action took the files: they're attached, never to be deleted from here. */
  const confirmed = useRef(false);
  /** A confirm on its way: whether it went through, once it's known. */
  const confirming = useRef<Promise<boolean> | null>(null);
  const mounted = useRef(true);
  /** Failed reads in a row of each pending check, by document id: how long to wait (R57). */
  const failures = useRef(new Map<string, number>());

  const update = useCallback((key: string, change: Partial<StagedItem>) => {
    if (!mounted.current) return;
    setItems((list) => list.map((item) => (item.key === key ? { ...item, ...change } : item)));
  }, []);

  const dropAll = useCallback(() => {
    discarded.current = true;
    for (const item of latest.current)
      if (item.document !== null) void unstageFile(item.document.id).catch(() => undefined);
  }, []);

  // Gone without a confirm (the page moved on under the step): what it staged goes too. A confirm
  // still on its way decides: refused, they go; saved, they're the action's. Cancel has already
  // given them up. Mounting again (React's dev double mount) starts the step afresh: nothing was
  // staged before it.
  useEffect(() => {
    mounted.current = true;
    discarded.current = false;
    return () => {
      mounted.current = false;
      if (confirmed.current || discarded.current) return;
      const pending = confirming.current;
      if (pending === null) dropAll();
      else
        void pending.then((ok) => {
          if (!ok) dropAll();
        });
    };
  }, [dropAll]);

  // R47: read each pending virus check again every 4 s, until it's done or the step closes. A read
  // that fails waits longer each time (8 s, 16 s, then every 30 s); a file the server no longer
  // has (404) isn't read again and holds the action until it's removed (R57).
  const pendingIds = items
    .flatMap((i) =>
      i.document !== null && i.document.scanStatus === 'pending' && !i.unreadable
        ? [i.document.id]
        : [],
    )
    .join(',');
  useEffect(() => {
    if (pendingIds === '') return;
    const controller = new AbortController();
    const timers = new Set<ReturnType<typeof setTimeout>>();
    const tried = failures.current;
    function read(id: string): void {
      readStagedFile(id, controller.signal).then(
        (document) => {
          if (controller.signal.aborted || !mounted.current) return;
          tried.delete(id);
          setItems((list) =>
            list.map((item) => (item.document?.id === document.id ? { ...item, document } : item)),
          );
          // Still pending: the list's ids are the same, so this run reads it again.
          if (document.scanStatus === 'pending') schedule(id);
        },
        (error: unknown) => {
          if (controller.signal.aborted || !mounted.current) return;
          if (isApiError(error) && error.status === 404) {
            tried.delete(id);
            setItems((list) =>
              list.map((item) => (item.document?.id === id ? { ...item, unreadable: true } : item)),
            );
            return;
          }
          tried.set(id, (tried.get(id) ?? 0) + 1);
          schedule(id);
        },
      );
    }
    function schedule(id: string): void {
      const timer = setTimeout(
        () => {
          timers.delete(timer);
          read(id);
        },
        scanPollDelay(tried.get(id) ?? 0),
      );
      timers.add(timer);
    }
    for (const id of pendingIds.split(',')) schedule(id);
    return () => {
      for (const timer of timers) clearTimeout(timer);
      controller.abort();
    };
  }, [pendingIds]);

  const live = items.filter((i) => i.state !== 'failed');
  const room = Math.max(0, MAX_FILES - live.length);

  const add = useCallback(
    (files: readonly File[], source: FileSource) => {
      const space = MAX_FILES - latest.current.filter((i) => i.state !== 'failed').length;
      for (const file of files.slice(0, Math.max(0, space))) {
        counter.current += 1;
        const key = `staged-${String(counter.current)}`;
        const tooBig = file.size > MAX_FILE_BYTES;
        const row: StagedItem = {
          key,
          name: file.name,
          sizeBytes: file.size,
          contentType: file.type,
          source,
          state: tooBig ? 'failed' : 'uploading',
          document: null,
          error: tooBig ? TOO_BIG : null,
          removing: false,
          unreadable: false,
        };
        latest.current = [...latest.current, row];
        setItems((list) => [...list, row]);
        if (tooBig) continue;
        stageFile(file).then(
          (document) => {
            // Removed, cancelled or gone while it was on its way up: it was never wanted.
            if (givenUp.current.has(key) || discarded.current || !mounted.current) {
              givenUp.current.delete(key);
              if (!confirmed.current) void unstageFile(document.id).catch(() => undefined);
              return;
            }
            update(key, { state: 'staged', document, name: document.fileName });
          },
          (error: unknown) => {
            update(key, { state: 'failed', error: actForProblemWords(error) });
          },
        );
      }
    },
    [update],
  );

  const remove = useCallback(
    async (key: string) => {
      const item = latest.current.find((i) => i.key === key);
      if (item === undefined) return;
      if (item.document === null) {
        // Still on its way up: deleted the moment it lands.
        if (item.state === 'uploading') givenUp.current.add(key);
        setItems((list) => list.filter((i) => i.key !== key));
        return;
      }
      update(key, { removing: true, error: null });
      try {
        await unstageFile(item.document.id);
        if (mounted.current) setItems((list) => list.filter((i) => i.key !== key));
      } catch (error) {
        // The server no longer has it (a check that couldn't be read 404s here too): it's gone.
        if (isApiError(error) && error.status === 404) {
          if (mounted.current) setItems((list) => list.filter((i) => i.key !== key));
          return;
        }
        update(key, { removing: false, error: actForProblemWords(error) });
      }
    },
    [update],
  );

  const confirm = useCallback(async <T>(save: () => Promise<T>): Promise<T> => {
    let settle: (ok: boolean) => void = () => undefined;
    confirming.current = new Promise<boolean>((resolve) => {
      settle = resolve;
    });
    try {
      const result = await save();
      confirmed.current = true;
      settle(true);
      return result;
    } catch (error) {
      settle(false);
      throw error;
    } finally {
      confirming.current = null;
    }
  }, []);

  const documents = items.flatMap((i) =>
    i.state === 'staged' &&
    i.document !== null &&
    !isBlocked(i.document.scanStatus) &&
    !i.unreadable
      ? [i.document]
      : [],
  );
  const busy = items.some((i) => i.state === 'uploading' || i.removing);
  const blocked = items.some(
    (i) => i.document !== null && (isBlocked(i.document.scanStatus) || i.unreadable),
  );
  return { items, add, remove, room, busy, blocked, documents, confirm, discard: dropAll };
}
